/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */
import PropTypes from 'prop-types';
import {
    CSSProperties,
    MouseEvent,
    MouseEventHandler,
    ReactNode,
    useEffect,
    useRef,
} from 'react';
import { styled } from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import Config from 'Config';

import { joinDataTestId, resolveComponentLocator } from '@motili/ui';
import classes from './Drawer.module.scss';

const DrawerHeader = styled.div`
    font-size: 21px;
    font-weight: 600;
`;
const DrawerSubHeader = styled.div`
    font-size: 16px;
    font-weight: 400;
    color: #818181;
`;

const DrawerFooter = styled.div`
    margin-top: auto;
`;

export interface DrawerProps {
    onClose: MouseEventHandler<HTMLElement>;
    open?: boolean;
    title: ReactNode;
    subTitle?: string;
    anchor?: string;
    children?: ReactNode;
    closeByDocument?: boolean;
    drawerStyle?: CSSProperties;
    drawerContainerStyle?: CSSProperties;
    inheritFooterStyles?: boolean;
    allowBackgroundScroll?: boolean;
    footer?: ReactNode;
    showClose?: boolean;
    dataTestId?: string;
    isAdminLegacyLocator?: boolean;
}

export default function Drawer(props: Readonly<DrawerProps>) {
    const {
        title,
        subTitle,
        open,
        anchor = 'right',
        onClose,
        closeByDocument,
        drawerStyle,
        drawerContainerStyle,
        showClose = true,
        inheritFooterStyles = true,
        allowBackgroundScroll,
        dataTestId,
        isAdminLegacyLocator = true,
    } = props;

    const {
        drawer,
        animate,
        hidden,
        overlay,
        overlayOpen,
        overlayHidden,
        header,
        headerAndClose,
        subTitleContainer,
    } = classes;

    const drawerRef = useRef<HTMLDivElement>(null);

    // This manages the scroll behavior of the drawer. If `allowBackgroundScroll` is false, it locks the page's scroll and maps it to the drawer instead.
    useEffect(() => {
        if (open && !allowBackgroundScroll) {
            const scrollY = window.scrollY;

            const originalPaddingRight = document.body.style.paddingRight;

            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflowY = 'hidden';
            document.body.style.paddingRight = '0px';

            return () => {
                const storedScrollY = Math.abs(
                    parseInt(document.body.style.top || '0', 10)
                );

                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflowY = '';
                document.body.style.paddingRight = originalPaddingRight; // restore

                requestAnimationFrame(() => {
                    window.scrollTo({
                        top: storedScrollY,
                        behavior: 'instant',
                    });
                });
            };
        }

        return undefined;
    }, [allowBackgroundScroll, open]);

    return (
        <>
            <div
                className={`${overlay} ${!open && overlayHidden} ${
                    open && overlayOpen
                }`}
                onClick={e => _onDocumentClick(e, { closeByDocument, onClose })}
                aria-hidden='true'
                style={{
                    zIndex: 20,
                }}
            />
            <div
                ref={drawerRef}
                style={{
                    backgroundColor: Config.color.white,
                    overflowY: 'auto',
                    ...drawerStyle,
                    zIndex: 20, // Set after dynamic styling to ensure z-index is uniform for all drawers
                }}
                tabIndex={-1}
                className={`${drawer} ${open && animate} ${
                    !open && hidden
                } ${changeAnchor(anchor, classes)}`}
                data-test-id={joinDataTestId([
                    dataTestId,
                    resolveComponentLocator(
                        isAdminLegacyLocator ? '' : 'drawer',
                        'drawer',
                        dataTestId
                    ),
                ])}
            >
                <div className={header}>
                    <div className={headerAndClose}>
                        <DrawerHeader data-test-id={joinDataTestId([dataTestId, 'drawer-header-label'])}>{title}</DrawerHeader>
                        {showClose && (
                            <IconButton onClick={onClose} edge='end'  data-test-id={joinDataTestId([dataTestId, 'drawer-close-button'])}>
                                <CloseIcon
                                    data-test-id='CloseIcon'
                                    style={{ width: 24, height: 24 }}
                                />
                            </IconButton>
                        )}
                    </div>

                    <div className={subTitleContainer}>
                        {subTitle && (
                            <DrawerSubHeader>{subTitle}</DrawerSubHeader>
                        )}
                    </div>
                </div>
                <div style={{ ...drawerContainerStyle }}>{props.children}</div>
                {props.footer &&
                    (inheritFooterStyles ? (
                        <DrawerFooter>{props.footer}</DrawerFooter>
                    ) : (
                        <div>{props.footer}</div>
                    ))}
            </div>
        </>
    );
}

Drawer.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.node.isRequired,
    anchor: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    closeByDocument: PropTypes.bool.isRequired,
};

function _onDocumentClick(
    e: MouseEvent<HTMLDivElement>,
    props: Pick<DrawerProps, 'closeByDocument' | 'onClose'>
) {
    if (props.closeByDocument === false) {
        return undefined;
    }
    return props.onClose(e);
}

const changeAnchor = (
    anchor: string,
    classes: {
        readonly [key: string]: string;
    }
) => {
    switch (anchor) {
        case 'left':
            return classes.left;
        case 'right':
            return classes.right;
        default:
            return classes.left;
    }
};
